import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useParams } from "react-router-dom";
import { appConstants } from "../_constants/app.constants";
import { isLikelyTournamentGuid, resolveTournamentIdFromPublicRoutes } from "../_utils/tournamentRouteUtils";
import { Badge, EmptyState, LoadingState, Toast, IconArrowLeft, IconTrophy } from "../ui";
import { directionLabel, formatGameValue, rankMedal } from "./gameUtils";
import { useTournamentPageView } from "../analytics/useTournamentPageView";

const TOP_N = 10;

function Leaderboard({ game }) {
  const entries = (game.entries ?? game.Entries ?? []).slice(0, TOP_N);
  const unit = game.unit ?? game.Unit ?? "";

  if (entries.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-ink-muted">
        Aún no hay marcas registradas en este juego.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-arena/15 text-left text-xs font-bold uppercase tracking-wide text-ink-muted">
            <th className="px-3 py-2 text-center">#</th>
            <th className="px-3 py-2">Jugador</th>
            <th className="px-3 py-2">Equipo</th>
            <th className="px-3 py-2 text-right">Marca</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const rank = e.rank ?? e.Rank;
            const medal = rankMedal(rank);
            return (
              <tr
                key={e.id ?? e.Id}
                className={`border-b border-arena/8 ${rank <= 3 ? "bg-gold/5" : ""}`}
              >
                <td className="px-3 py-2 text-center font-bold text-ink">
                  {medal ? <span className="mr-0.5">{medal}</span> : null}
                  {rank}
                </td>
                <td className="px-3 py-2 font-semibold text-ink">{e.playerName ?? e.PlayerName}</td>
                <td className="px-3 py-2 text-ink-muted">{e.teamName ?? e.TeamName ?? "—"}</td>
                <td className="px-3 py-2 text-right font-display font-extrabold text-arena">
                  {formatGameValue(e.value ?? e.Value)}
                  {unit ? <span className="ml-1 text-xs font-semibold text-ink-muted">{unit}</span> : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function TournamentGamesPage() {
  const { tournamentId: routeKey } = useParams();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canonicalId, setCanonicalId] = useState(null);
  useTournamentPageView(canonicalId, "games");

  const API = appConstants.API_URL;
  // Once we've shown a leaderboard at least once, a transient poll failure must
  // not blank the screen — keep the last good data instead of flipping to error.
  const loadedOnceRef = useRef(false);

  // 1) Resolve the canonical tournament id from the route (accepts a guid or a
  //    public slug). Polling lives in the next effect, keyed on the resolved id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const trimmed = routeKey ? String(routeKey).trim() : "";
      if (!trimmed) {
        setError("Torneo no especificado.");
        setLoading(false);
        return;
      }
      if (isLikelyTournamentGuid(trimmed)) {
        if (!cancelled) setCanonicalId(trimmed);
        return;
      }
      try {
        const resolved = await resolveTournamentIdFromPublicRoutes(API, trimmed);
        if (cancelled) return;
        if (!resolved) {
          setError("No se encontró el torneo.");
          setLoading(false);
          return;
        }
        setCanonicalId(resolved);
      } catch {
        if (!cancelled) {
          setError("No se pudieron cargar los juegos.");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeKey, API]);

  // 2) Fetch the games for the resolved id, then poll so new marks/results show
  //    up on their own — same live-refresh behaviour as grupos/partidos/bracket.
  useEffect(() => {
    if (!canonicalId) return undefined;

    let cancelled = false;
    loadedOnceRef.current = false;

    const fetchGames = async () => {
      try {
        const res = await axios.get(`${API}/api/tournamentgames/${canonicalId}/games`);
        if (cancelled) return;
        setGames(Array.isArray(res.data) ? res.data : []);
        setError("");
        loadedOnceRef.current = true;
      } catch (err) {
        if (cancelled || loadedOnceRef.current) return; // keep last good data on a poll blip
        const status = err.response?.status;
        setError(
          status === 404
            ? "Este torneo no tiene juegos públicos o no está disponible."
            : "No se pudieron cargar los juegos."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchGames();
    const interval = setInterval(fetchGames, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [canonicalId, API]);

  const backTo = canonicalId ? `/tournamentactions/${canonicalId}` : `/tournamentactions/${routeKey}`;

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <RouterLink
        to={backTo}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-ink-muted hover:text-arena"
      >
        <IconArrowLeft size={16} />
        Volver al torneo
      </RouterLink>

      <header className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/15 text-gold-dim">
          <IconTrophy size={24} />
        </span>
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">Juegos del torneo</h1>
          <p className="text-sm text-ink-muted">Retos y marcas de los participantes. Top {TOP_N} por juego.</p>
        </div>
      </header>

      {loading ? <LoadingState className="min-h-[30vh]" label="Cargando juegos…" /> : null}
      {error && !loading ? <Toast kind="error">{error}</Toast> : null}

      {!loading && !error && games.length === 0 ? (
        <EmptyState
          title="Todavía no hay juegos"
          description="La organización aún no ha publicado retos para este torneo. Vuelve más tarde."
        />
      ) : null}

      {!loading && !error && games.length > 0 ? (
        <div className="flex flex-col gap-5">
          {games.map((g) => {
            const unit = g.unit ?? g.Unit ?? "";
            const higher = g.higherIsBetter ?? g.HigherIsBetter ?? true;
            const desc = g.description ?? g.Description ?? "";
            return (
              <section
                key={g.id ?? g.Id}
                className="overflow-hidden rounded-2xl border border-arena/12 bg-white shadow-sm"
              >
                <div className="flex flex-wrap items-start justify-between gap-2 border-b border-arena/10 bg-arena/[0.03] px-4 py-3">
                  <div className="min-w-0">
                    <h2 className="font-display text-lg font-extrabold text-ink">{g.name ?? g.Name}</h2>
                    {desc ? <p className="mt-0.5 text-sm text-ink-muted">{desc}</p> : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {unit ? <Badge variant="neutral">{unit}</Badge> : null}
                    <Badge variant={higher ? "success" : "info"}>{directionLabel(higher)}</Badge>
                  </div>
                </div>
                <Leaderboard game={g} />
              </section>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
